import React, { useContext, useRef } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { QueryClientProvider } from '@tanstack/react-query';

import { BONavbar } from './BONavbar';
import { TopNavRow } from './TopNavRow';
import { IAuth } from '../../../Auth';
import { useCollectContext } from '../../../collect/context';
import { useBFCache } from '../../../hooks';
import { EColor } from '../../../Theme/components/colors';
import { defaultQueryClient, maxWidths } from '../../../utilities';
import { NavigationContext } from '../../context/NavigationContext';
import { INavigationItem } from '../../types/navigation';

export interface INavRowsProps {
  auth?: IAuth;
  navItems?: INavigationItem[];
  pastSearchBaseUrl: string;
  manageAccountUrl: string | undefined;
  isInitializing: boolean;
  showCartButton?: boolean;
  enableOfferNotificationBadges?: boolean;
  enableCollections?: boolean;
}

export function NavRows({
  auth,
  navItems,
  pastSearchBaseUrl,
  manageAccountUrl,
  isInitializing,
  showCartButton,
  enableOfferNotificationBadges,
  enableCollections,
}: INavRowsProps) {
  const [show, setShow] = React.useState(false);
  const { stickyTop } = useCollectContext();
  const { enableNewNav } = useContext(NavigationContext);
  const closeMenu = () => setShow(false);
  const handleToggle = () => setShow(!show);
  const navRef = useRef<HTMLDivElement>(null);

  useBFCache(closeMenu);

  return (
    <Flex
      alignItems="center"
      backdropFilter={enableNewNav ? undefined : 'saturate(1.8) blur(20px)'}
      backgroundColor={enableNewNav ? EColor.White : 'rgba(255, 255, 255, 0.8)'}
      borderBottom="1px solid"
      borderColor={EColor.Neutral150}
      boxShadow={enableNewNav ? undefined : '0px 3px 9px rgba(0, 0, 0, 0.05)'}
      color={EColor.Black}
      flexDirection="column"
      justify="space-between"
      paddingBottom="8px"
      paddingTop={enableNewNav ? '8px' : '16px'}
      position={{ base: undefined, lg: 'sticky' }}
      ref={navRef}
      top={{ base: '0', lg: stickyTop ?? '0' }}
      width="100%"
      zIndex="1030"
    >
      <Box maxWidth={maxWidths} width="100%">
        {enableNewNav ? (
          <QueryClientProvider client={defaultQueryClient}>
            <BONavbar
              auth={auth}
              closeMenu={closeMenu}
              enableCollections={enableCollections}
              enableOfferNotificationBadges={enableOfferNotificationBadges}
              handleToggle={handleToggle}
              isInitializing={isInitializing}
              manageAccountUrl={manageAccountUrl}
              navRef={navRef}
              show={show}
              showCartButton={showCartButton}
            />
          </QueryClientProvider>
        ) : (
          <TopNavRow
            auth={auth}
            closeMenu={closeMenu}
            enableOfferNotificationBadges={enableOfferNotificationBadges}
            handleToggle={handleToggle}
            isInitializing={isInitializing}
            manageAccountUrl={manageAccountUrl}
            navItems={navItems}
            pastSearchBaseUrl={pastSearchBaseUrl}
            show={show}
            showCartButton={showCartButton}
          />
        )}
      </Box>
    </Flex>
  );
}
